import { EMPTY_ARRAY } from '@talos/kyoko';
import type { Dispatch, SetStateAction } from 'react';
import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useMarketSelectorConfig, type MarketSelectorConfigContextProps } from './MarketSelectorConfigContext';
import type { MarketSelectorAPI, MarketSelectorExtension, MarketSelectorExtensionFactory } from './types';
import { hasAdvancedOnlyColumns } from './utils/enrichments';

export interface MarketSelectorContextProps extends MarketSelectorAPI, MarketSelectorConfigContextProps {
  blotterSearchInput: HTMLInputElement | null;
  setBlotterSearchInput: React.Dispatch<SetStateAction<HTMLInputElement | null>>;
  /** Whether or not to show things related to the advanced mode, such as the Advanced Mode toggle */
  showAdvancedMode: boolean;
  showCredentialsOnlyToggle: boolean;
  /** If set to true, the view will not render anything related to aggregations */
  disableAggregations: boolean;
  /** Interactive view extensions materialized by this provider from the Market Selector API. */
  extensions: MarketSelectorExtension[];
  /** State that applies only in narrow mode */
  narrowModeConfig: {
    /** Show market selector blotter in narrow mode (hide aggregations) */
    showMarketSelectorBlotter: boolean;
    setShowMarketSelectorBlotter: (showMarketSelectorBlotter: boolean) => void;
  };
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const MarketSelectorContext = createContext<MarketSelectorContextProps | undefined>(undefined);

export function useMarketSelectorContext() {
  const context = useContext(MarketSelectorContext);
  if (context === undefined) {
    throw new Error('Missing MarketSelectorContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

type MarketSelectorContextProviderProps = {
  marketSelectorAPI: MarketSelectorAPI;
  showCredentialsOnlyToggle?: boolean;
  disableAggregations?: boolean;
  extensions?: MarketSelectorExtensionFactory[];
  isNarrow: boolean;
  children: ReactNode;
};

/**
 * This provider exists to make it easier to consume the state and api returned from the useMarketSelector hook as well as common state
 * which can be useful for several views.
 */
export const MarketSelectorContextProvider = function MarketSelectorContextProvider({
  children,
  marketSelectorAPI,
  isNarrow,
  showCredentialsOnlyToggle = false,
  disableAggregations = false,
  extensions: extensionFactories = EMPTY_ARRAY,
}: MarketSelectorContextProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { preset } = marketSelectorAPI;
  // We want to initially show the market selector blotter, in narrow mode if there is no preset selected
  const [showMarketSelectorBlotter, setShowMarketSelectorBlotter] = useState(preset == null);
  const {
    advancedMode: inputAdvancedMode,
    setAdvancedMode,
    showDisabledItems,
    setShowDisabledItems,
    credentialsOnly: inputCredentialsOnly,
    setCredentialsOnly,
    selectionListShowAll,
    setSelectionListShowAll,
  } = useMarketSelectorConfig();

  const extensions = useMemo(
    () => extensionFactories.map(extensionFactory => extensionFactory(marketSelectorAPI)),
    [extensionFactories, marketSelectorAPI]
  );
  const extensionValueChangeListener = useMemo(
    () => (value: Parameters<NonNullable<MarketSelectorExtension['onValueChange']>>[0]) => {
      for (const extension of extensions) {
        extension.onValueChange?.(value);
      }
    },
    [extensions]
  );
  useLayoutEffect(() => {
    marketSelectorAPI.setExtensionValueChangeListener(extensionValueChangeListener);
    for (const extension of extensions) {
      extension.onValueCommitted?.();
    }
    return () => marketSelectorAPI.setExtensionValueChangeListener(undefined);
  }, [extensionValueChangeListener, extensions, marketSelectorAPI]);
  const showAdvancedMode = hasAdvancedOnlyColumns([...marketSelectorAPI.enrichments, ...extensions]);
  const advancedMode = inputAdvancedMode && showAdvancedMode;

  // This boolean can only be true if the toggle itself is even shown to the user
  const credentialsOnly = showCredentialsOnlyToggle && inputCredentialsOnly;

  const [blotterSearchInput, setBlotterSearchInput] = useState<HTMLInputElement | null>(null);
  const value = useMemo(
    () => ({
      showAdvancedMode,
      advancedMode,
      setAdvancedMode,
      showDisabledItems,
      setShowDisabledItems,
      credentialsOnly,
      setCredentialsOnly,
      selectionListShowAll,
      setSelectionListShowAll,
      showCredentialsOnlyToggle,
      disableAggregations,
      extensions,
      narrowModeConfig: {
        showMarketSelectorBlotter,
        setShowMarketSelectorBlotter,
      },
      isOpen,
      setIsOpen,
      ...marketSelectorAPI,
      isNarrow,
      blotterSearchInput,
      setBlotterSearchInput,
    }),
    [
      showAdvancedMode,
      advancedMode,
      setAdvancedMode,
      showDisabledItems,
      setShowDisabledItems,
      credentialsOnly,
      showCredentialsOnlyToggle,
      disableAggregations,
      extensions,
      setCredentialsOnly,
      selectionListShowAll,
      setSelectionListShowAll,
      marketSelectorAPI,
      showMarketSelectorBlotter,
      setShowMarketSelectorBlotter,
      isNarrow,
      isOpen,
      setIsOpen,
      blotterSearchInput,
      setBlotterSearchInput,
    ]
  );
  return <MarketSelectorContext.Provider value={value}>{children}</MarketSelectorContext.Provider>;
};
